import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@components/Community/index.js';
import {
    IconLogout,
    IconSettings,
    IconRosetteDiscountCheckFilled,
    IconSun,
    IconMoon,
    IconDeviceDesktop,
    IconLanguage,
    IconSelector,
} from "@tabler/icons-react";
import { Text, ButtonGroup, Popper } from '@fragments';
import { appVersion, termsUrl } from '@config';
import './dropdown.scss';

// The picker's search matches the language's own name plus a few aliases, so
// "spanish", "espanhol" and "es-es" all find Español.
const searchLanguage = (option, validate) => validate(option.label) || validate(option.keywords);

export default function UserDropdown({
    user,
    appearance,
    onChangeTheme,
    language,
    languages = [],
    onChangeLanguage,
    onLanguageMenuOpenChange,
    languageMenuPlacement = 'right-start',
    items = [],
    onOpenProfile,
    onViewProfile,
    onLogout,
}) {
    const { t } = useTranslation('shell');

    const languagePopperRef = useRef(null);
    const languageRowRef = useRef(null);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

    const currentLanguage = languages.find(l => l.code === language) || languages[0];

    const handleLanguageMenuOpenChange = useCallback((isOpen) => {
        setLanguageMenuOpen(isOpen);
        onLanguageMenuOpenChange?.(isOpen);
    }, [onLanguageMenuOpenChange]);

    function handleLanguageClick() {
        if (languageMenuOpen) {
            languagePopperRef.current?.close();
            return;
        }

        languagePopperRef.current?.open(
            languages.map(l => ({
                id: l.code,
                label: l.label,
                keywords: l.keywords,
                icon: (
                    <Text.caption weight={600} contrast={7} className="usd-lang-code">
                        {l.short}
                    </Text.caption>
                ),
                active: l.code === language,
                onClick: () => onChangeLanguage?.(l.code),
            })),
            languageMenuPlacement,
            languageRowRef.current,
        );
    }

    return (
        <div className="usd-panel">
            {/* User card — the whole header opens the public profile */}
            <button
                type="button"
                className="usd-user-card"
                onClick={onViewProfile}
                title={user?.username}
            >
                <UserAvatar user={user} size={44} link={false} />
                <div className="usd-user-info">
                    <div className="usd-username-row">
                        <Text.span weight={500} contrast={10}>{user?.username}</Text.span>
                        {user?.verified ? (
                            <IconRosetteDiscountCheckFilled size={16} className="usd-verified-icon" />
                        ) : null}
                    </div>
                    <Text.span contrast={6} className="usd-sub-text">
                        {user?.nickname ? `@${user?.nickname}` : user?.email?.address}
                    </Text.span>
                </div>
            </button>

            <div className="usd-divider" />

            {/* Appearance row */}
            <div className="usd-row usd-row--no-hover">
                <Text.span contrast={7}>{t('userMenu.appearance')}</Text.span>
                <ButtonGroup
                    size="small"
                    value={appearance}
                    onChange={onChangeTheme}
                    options={[
                        { value: 'light',  icon: <IconSun  size={15} /> },
                        { value: 'system', icon: <IconDeviceDesktop size={15} /> },
                        { value: 'dark',   icon: <IconMoon size={15} /> },
                    ]}
                />
            </div>

            {/* Language row — opens a searchable sub-popper beside the panel */}
            <button
                ref={languageRowRef}
                className={`usd-row usd-row--button${languageMenuOpen ? ' usd-row--open' : ''}`}
                onClick={handleLanguageClick}
                aria-haspopup="listbox"
                aria-expanded={languageMenuOpen}
            >
                <div className="usd-row-left">
                    <Text.span left={<IconLanguage />} contrast={9}>{t('userMenu.language')}</Text.span>
                </div>
                <div className="usd-row-right">
                    <Text.span contrast={6}>{currentLanguage?.label}</Text.span>
                    <IconSelector size={14} className="usd-row-chevron" />
                </div>
            </button>
            <Popper
                controlRef={languagePopperRef}
                config={{ searchKeyExtractor: searchLanguage }}
                onOpenChange={handleLanguageMenuOpenChange}
                paperSx={{ minWidth: 224 }}
            />

            <div className="usd-divider" />

            {/* Host rows (e.g. the Studio's subscriptions), then settings */}
            {items.map(item => (
                <button key={item.id} type="button" className="usd-row usd-row--button" onClick={item.onClick}>
                    <div className="usd-row-left">
                        <Text.span left={item.icon} contrast={9}>{item.label}</Text.span>
                    </div>
                </button>
            ))}
            <button className="usd-row usd-row--button" onClick={onOpenProfile}>
                <div className="usd-row-left">
                    <Text.span left={<IconSettings />} contrast={9}>{t('userMenu.profileSettings')}</Text.span>
                </div>
            </button>

            <div className="usd-divider" />

            {/* Logout */}
            <button className="usd-row usd-row--button usd-row--danger" onClick={onLogout}>
                <div className="usd-row-left">
                    <Text.span left={<IconLogout />} contrast={9}>{t('userMenu.logout')}</Text.span>
                </div>
            </button>

            {/* Footer */}
            <div className="usd-footer">
                <Text.span contrast={5} className="usd-footer-text">
                    v{appVersion} · <a href={termsUrl} target="_blank" rel="noreferrer">{t('userMenu.terms')}</a>
                </Text.span>
            </div>
        </div>
    );
}
